import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import Transition from 'react-transition-group/Transition'
import uuid from 'uuid/v4'
import withResponsiveProps from 'decorators/Responsive'
import Div from 'components/Div'
import { createPortal } from 'components/Portal'
import FocusTrap from 'components/FocusTrap'
import ModalBackdrop from './ModalBackdrop'
import ModalContainer from './ModalContainer'
import WindowEvents from 'decorators/WindowEvents'
import * as Elements from './Elements'
import * as bodyScrolling from 'tools/bodyScrolling'

const ModalPortal = createPortal()

export const ModalPortalTarget = ModalPortal.Target

const ModalWrapper = styled(Div)`
  position: fixed;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  overflow: auto;
  z-index: 9;
  pointer-events: none;
`

class Modal extends React.PureComponent {
  static propTypes = {
    show: PropTypes.bool,
    onEnter: PropTypes.func,
    onEntered: PropTypes.func,
    onClose: PropTypes.func, // callback to be invoked before Modal is closing
    onClosed: PropTypes.func, // callback to be invoked after Modal is closed
    Container: PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.string]),
    Backdrop: PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.string]),
    withoutBackdrop: PropTypes.bool,
    closeOnBackdropClick: PropTypes.bool,
    closeOnEscapeKeyPress: PropTypes.bool,
    onBackdropClick: PropTypes.func,
    transitionDuration: PropTypes.number,
    withoutFocusTrap: PropTypes.bool,
    slideInAnimation: PropTypes.string,
    registerEventListener: PropTypes.func.isRequired, // injected by WindowEvents
    unmountOnExit: PropTypes.bool,
    initialFocus: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.element])
  }

  static defaultProps = {
    show: false,
    Container: ModalContainer,
    Backdrop: ModalBackdrop,
    closeOnBackdropClick: true,
    closeOnEscapeKeyPress: true,
    withoutBackdrop: false,
    onBackdropClick: () => {},
    onEnter: () => {},
    onEntered: () => {},
    onClose: () => {},
    onClosed: () => {},
    transitionDuration: 300,
    withoutFocusTrap: false,
    unmountOnExit: true
  }

  constructor (props) {
    super(props)
    props.registerEventListener('keydown', this.handleEscapeKeyPress, false)
    this.id = uuid()
  }

  componentDidMount () {
    if (this.props.show) {
      this.lockBodyScroll()
    }
  }

  componentDidUpdate (prevProps, prevState) {
    const { withoutBackdrop, show } = this.props
    if (prevProps.show !== show || prevProps.withoutBackdrop !== withoutBackdrop) {
      if (show && !withoutBackdrop) {
        this.lockBodyScroll()
      } else {
        this.releaseBodyScroll()
      }
    }
  }

  componentWillUnmount () {
    this.releaseBodyScroll()
  }

  lockBodyScroll = () => !this.props.withoutBackdrop && bodyScrolling.lock(this.id)

  releaseBodyScroll = () => bodyScrolling.release(this.id)

  closeModal = () => this.props.onClose()

  handleEscapeKeyPress = (e) => {
    const { closeOnEscapeKeyPress } = this.props
    if (closeOnEscapeKeyPress && e.keyCode === 27) {
      this.closeModal()
    }
  }

  handleBackdropClick = () => {
    const { closeOnBackdropClick, onBackdropClick } = this.props
    closeOnBackdropClick && this.closeModal()
    onBackdropClick()
  }

  render () {
    const { show, className, children, onClosed, onEnter, onEntered, transitionDuration, unmountOnExit, initialFocus } = this.props
    const { Backdrop, withoutBackdrop } = this.props
    const { Container, slideInAnimation } = this.props
    const { withoutFocusTrap } = this.props

    return (
      <ModalPortal.Source>
        <Transition in={show} timeout={transitionDuration} onEnter={onEnter} onEntered={onEntered} onExited={onClosed} mountOnEnter unmountOnExit={unmountOnExit} appear>
          <ModalWrapper className={className} withoutBackdrop={withoutBackdrop}>
            {!withoutBackdrop && <Backdrop show={show} onClick={this.handleBackdropClick} transitionDuration={transitionDuration} />}
            <FocusTrap disabled={withoutFocusTrap} initialFocus={initialFocus}>
              <Container
                show={show}
                slideInAnimation={slideInAnimation}
                transitionDuration={transitionDuration}>
                {children}
              </Container>
            </FocusTrap>
          </ModalWrapper>
        </Transition>
      </ModalPortal.Source>
    )
  }
}

const ResponsiveModal = withResponsiveProps(WindowEvents(Modal), ['slideInAnimation', 'transitionDuration', 'withoutBackdrop', 'show', 'withoutFocusTrap'])
ResponsiveModal.Container = Elements.Container
ResponsiveModal.FlexContainer = Elements.FlexContainer
ResponsiveModal.Header = Elements.ModalHeader
ResponsiveModal.Body = Elements.ModalBody

ResponsiveModal.Section = Elements.ModalSection
ResponsiveModal.Footer = Elements.ModalFooter

export default ResponsiveModal
